import {
    mapMfaDuplicateCallMitigationConfig,
    MfaDuplicateCallMitigationConfig,
    MfaDuplicateCallMitigationConfigDto,
} from '@accountFederation/config/mfaDuplicateMitigationConfigMapper';
import { ResponsibleGamingPageElement } from '@src/config/shell/localConfigTypes';
import { getRawRemoteConfig } from '@superbet-group/web.lib.config';

let configuration: AccountConfig | undefined;

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getAccountConfig(
    remoteConfig: AccountRemoteConfigDto = getRawRemoteConfig() as AccountRemoteConfigDto,
): AccountConfig {
    if (!configuration) {
        configuration = {
            isNewLoginHeaderEnabled: remoteConfig.isNewLoginHeaderEnabled || false,
            isNewActivationEmailPageEnabled: remoteConfig.isNewActivationEmailPageEnabled || false,
            isGetIdPendingStateUsed: remoteConfig.isGetIdPendingStateUsed || false,
            hasGetIdKyc: remoteConfig.hasGetIdKyc || false,
            getIdMobileHeightDiff: remoteConfig.getIdMobileHeightDiff || 184,
            isCanonKycEnabled: remoteConfig.isCanonKycEnabled || false,
            isRemainingDaysVisible: remoteConfig.isRemainingDaysVisible || false,
            isZendeskAuthenticationEnabled: remoteConfig.isZendeskAuthenticationEnabled || false,
            useZendeskTimeout: remoteConfig.useZendeskTimeout || false,
            zendeskTimeoutValue: remoteConfig.zendeskTimeoutValue || '1000',
            zendeskLoadingTimeCheck: remoteConfig.zendeskLoadingTimeCheck || 10000,
            idCardVerificationHubHelpUrl: remoteConfig.idCardVerificationHubHelpUrl || '',
            kycDetailsTimeCheck: remoteConfig.kycDetailsTimeCheck || 5000,
            responsibleGamingElementsOverride: remoteConfig.responsibleGamingElementsOverride || {},
            isChangePhoneVisibleEveryTime: remoteConfig.isChangePhoneVisibleEveryTime ?? true,
            isChangePhoneDisabled: remoteConfig.isChangePhoneDisabled ?? true,
            shouldWaitForWafToken: remoteConfig.shouldWaitForWafToken ?? false,
            useKycLocalStoragePendingState: remoteConfig.useKycLocalStoragePendingState ?? false,
            isKycRemainingDaysServerSide: remoteConfig.isKycRemainingDaysServerSide ?? true,
            hasKycCaptive: remoteConfig.hasKycCaptive ?? false,
            hasVerifyPhoneCaptive: remoteConfig.hasVerifyPhoneCaptive ?? false,
            hasGenderAndNationalityRequirement: remoteConfig.hasGenderAndNationalityRequirement ?? false,
            hasAccountMenuRgInfo: remoteConfig.hasAccountMenuRgInfo ?? false,
            hasAnnualDataCollection: remoteConfig.hasAnnualDataCollection ?? false,
            hasNewPhoneVerificationMenuItem: remoteConfig.hasNewPhoneVerificationMenuItem ?? false,
            licenceFaqItems: remoteConfig.licenceFaqItems?.questionIdentifiers ?? [],
            isForgotPasswordSecurityCheckEnabled: remoteConfig.isForgotPasswordSecurityCheckEnabled || false,
            faceIdFlowName: remoteConfig.faceIdFlowName || '',
            areSitesSplitByLicence: remoteConfig.areSitesSplitByLicence || false,
            vanHeckeDateOverride: remoteConfig.vanHeckeDateOverride
                ? new Date(remoteConfig.vanHeckeDateOverride)
                : undefined,
            hasNewLoginRegisterButtons: false,
            isUploadAdditionalVisibleOnlyForVerified: remoteConfig.isUploadAdditionalVisibleOnlyForVerified || false,
            hasBrMigrationLoginNotice: remoteConfig.hasBrMigrationLoginNotice || false,
            brLoginNoticeUrl: remoteConfig.brLoginNoticeUrl || '',
            isMfaEnabled: remoteConfig.isMfaEnabled || false,
            isAntifraudLoggingEnabled: remoteConfig.isAntifraudLoggingEnabled || false,
            isKycSuccessRedirectToDepositEnabled: remoteConfig.isKycSuccessRedirectToDepositEnabled || false,
            mfaDuplicateCallMitigationConfig: mapMfaDuplicateCallMitigationConfig(remoteConfig.mfaDuplicateCallMitigationConfig),
            isFailedLoginCounterEnabled: remoteConfig.isFailedLoginCounterEnabled || false,
            mfaCheckStatusInterval: remoteConfig.mfaCheckStatusInterval || 3000,
            mfaCheckStatusMinTimeoutMs: remoteConfig.mfaCheckStatusMinTimeoutMs || 100,
            mfaCheckStatusMaxTimeoutMs: remoteConfig.mfaCheckStatusMaxTimeoutMs || 3000,
            isEmailLoginOtp: remoteConfig.isEmailLoginOtp || false,
            isOtpSuggestionFixEnabled: remoteConfig.isOtpSuggestionFixEnabled || false,
            hasUpliftedVerifyPhone: remoteConfig.hasUpliftedVerifyPhone !== false,
            isAccountSecuritySettingsEnabled: remoteConfig.isAccountSecurityEnabled || false,
        };
    }

    return configuration;
}

type AccountRemoteConfigDto = {
    isNewLoginHeaderEnabled?: boolean;
    isNewActivationEmailPageEnabled?: boolean;
    isGetIdPendingStateUsed?: boolean;
    isCanonKycEnabled?: boolean;
    isRemainingDaysVisible?: boolean;
    hasGetIdKyc?: boolean;
    getIdMobileHeightDiff?: number;
    isZendeskAuthenticationEnabled?: boolean;
    useZendeskTimeout?: boolean;
    zendeskTimeoutValue?: string;
    zendeskLoadingTimeCheck?: number;
    idCardVerificationHubHelpUrl?: string;
    kycDetailsTimeCheck?: number;
    responsibleGamingElementsOverride?: Partial<Record<ResponsibleGamingPageElement, boolean>>;
    isChangePhoneVisibleEveryTime?: boolean;
    isChangePhoneDisabled?: boolean;
    shouldWaitForWafToken?: boolean;
    useKycLocalStoragePendingState?: boolean;
    isKycRemainingDaysServerSide?: boolean;
    hasKycCaptive?: boolean;
    hasVerifyPhoneCaptive?: boolean;
    hasGenderAndNationalityRequirement?: boolean;
    hasAccountMenuRgInfo?: boolean;
    hasAnnualDataCollection?: boolean;
    hasNewPhoneVerificationMenuItem?: boolean;
    licenceFaqItems?: {
        questionIdentifiers: string[];
    };
    areSitesSplitByLicence?: boolean;
    vanHeckeDateOverride?: string;
    hasNewLoginRegisterButtons?: boolean;
    isForgotPasswordSecurityCheckEnabled?: boolean;
    faceIdFlowName?: string;
    isUploadAdditionalVisibleOnlyForVerified?: boolean;
    hasBrMigrationLoginNotice?: boolean;
    brLoginNoticeUrl?: string;
    isMfaEnabled?: boolean;
    isAntifraudLoggingEnabled?: boolean;
    isKycSuccessRedirectToDepositEnabled?: boolean;
    mfaDuplicateCallMitigationConfig?: MfaDuplicateCallMitigationConfigDto;
    isFailedLoginCounterEnabled?: boolean;
    mfaCheckStatusInterval?: number;
    mfaCheckStatusMinTimeoutMs: number;
    mfaCheckStatusMaxTimeoutMs: number;
    isEmailLoginOtp?: boolean;
    isOtpSuggestionFixEnabled?: boolean;
    hasUpliftedVerifyPhone?: boolean;
    isAccountSecurityEnabled?: boolean;
};

type AccountConfig = {
    isNewLoginHeaderEnabled: boolean;
    isNewActivationEmailPageEnabled: boolean;
    isGetIdPendingStateUsed: boolean;
    isCanonKycEnabled: boolean;
    isRemainingDaysVisible: boolean;
    hasGetIdKyc: boolean;
    getIdMobileHeightDiff: number;
    isZendeskAuthenticationEnabled: boolean;
    useZendeskTimeout: boolean;
    zendeskTimeoutValue: string;
    zendeskLoadingTimeCheck: number;
    idCardVerificationHubHelpUrl?: string;
    kycDetailsTimeCheck: number;
    responsibleGamingElementsOverride: Partial<Record<ResponsibleGamingPageElement, boolean>>;
    isChangePhoneVisibleEveryTime: boolean;
    isChangePhoneDisabled: boolean;
    shouldWaitForWafToken: boolean;
    useKycLocalStoragePendingState: boolean;
    isKycRemainingDaysServerSide: boolean;
    hasKycCaptive: boolean;
    hasVerifyPhoneCaptive: boolean;
    hasGenderAndNationalityRequirement: boolean;
    hasAccountMenuRgInfo: boolean;
    hasAnnualDataCollection: boolean;
    hasNewPhoneVerificationMenuItem: boolean;
    licenceFaqItems: string[];
    areSitesSplitByLicence: boolean;
    vanHeckeDateOverride?: Date;
    hasNewLoginRegisterButtons?: boolean;
    isForgotPasswordSecurityCheckEnabled: boolean;
    faceIdFlowName: string;
    isUploadAdditionalVisibleOnlyForVerified: boolean;
    hasBrMigrationLoginNotice: boolean;
    brLoginNoticeUrl: string;
    isMfaEnabled: boolean;
    isAntifraudLoggingEnabled: boolean;
    isKycSuccessRedirectToDepositEnabled: boolean;
    mfaDuplicateCallMitigationConfig: MfaDuplicateCallMitigationConfig;
    isFailedLoginCounterEnabled: boolean;
    mfaCheckStatusInterval: number;
    mfaCheckStatusMinTimeoutMs: number;
    mfaCheckStatusMaxTimeoutMs: number;
    isEmailLoginOtp: boolean;
    isOtpSuggestionFixEnabled: boolean;
    hasUpliftedVerifyPhone: boolean;
    isAccountSecuritySettingsEnabled: boolean;
};
